import { useEffect, useMemo, useState } from 'react';
import { EditorSidebarSection, EditorSidebarCard } from '@/components/editor';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { sv } from 'date-fns/locale';
import { useQueryClient } from '@tanstack/react-query';
import {
  Calendar as CalendarIcon,
  CalendarDays,
  ChevronRight,
  Eye,
  EyeOff,
  ImageIcon,
  ImagePlus,
  Info,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StickyBottomBar } from '@/components/ui/StickyBottomBar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { MediaImageManager } from '@/components/media/MediaImageManager';
import { FleaMarketPreviewCard } from '@/components/flea-market/FleaMarketPreviewCard';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useFleaMarketRollout } from '@/hooks/useFleaMarketRollout';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  useCreateFleaMarket,
  useCreateFleaMarketOccurrencesBulk,
  useDeleteFleaMarket,
  useFleaMarketOccurrences,
  useProducerFleaMarkets,
  useCreateFleaMarketOccurrence,
  useDeleteFleaMarketOccurrence,
  useUpdateFleaMarket,
  useUpdateFleaMarketOccurrence,
} from '@/hooks/useProducerFleaMarkets';
import {
  buildBulkOccurrencePayloads,
  buildOccurrenceEditPatch,
  filterBulkDuplicates,
  formatOccurrenceRangeSv,
  getNextUpcomingOccurrence,
  getOccurrenceUiLabel,
  getOccurrenceUiStatus,
  hasInvalidTimeRange,
  sortOccurrencesNearestFirst,
} from '@/lib/fleaMarkets/occurrences';
import { normalizeTiptapHtml } from '@/lib/richtext/normalizeTiptapHtml';
import { cn } from '@/lib/utils';
import { clearDraftSessionId, useDraftSessionId } from '@/hooks/useDraftSessionId';
import { finalizeDraftSession, resolveAssetUrl, deleteAsset, listAssetsByDraftSession } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';
import { useFleaMarketOccurrenceDraftGalleryAssets, useFleaMarketOccurrenceGalleryAssets } from '@/hooks/useFleaMarketOccurrenceMedia';

function OccurrenceImagesField({
  assets,
  disabled,
  isUploading,
  onUpload,
  onRemove,
  helperText,
  inputId,
}: {
  assets: MediaAsset[];
  disabled: boolean;
  isUploading: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onRemove: (assetId: string) => Promise<void>;
  helperText: string;
  inputId: string;
}) {
  const imageLimit = 8;
  const atLimit = assets.length >= imageLimit;

  return (
    <div className="space-y-2">
      <Label>Bilder för detta tillfälle (valfritt)</Label>
      <p className="text-xs text-muted-foreground">{helperText}</p>

      {assets.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {assets.map((asset) => {
            const imageUrl = resolveAssetUrl(asset, 320) ?? resolveAssetUrl(asset, 640);
            return (
              <div key={asset.id} className="relative rounded-md border border-border/50 overflow-hidden bg-muted aspect-square">
                {imageUrl ? (
                  <img src={imageUrl} alt={asset.alt_text ?? 'Tillfällesbild'} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">Bild</div>
                )}
                <button
                  type="button"
                  onClick={() => void onRemove(asset.id)}
                  disabled={disabled}
                  className="absolute top-1 right-1 rounded-full bg-background/90 border border-border p-1 text-muted-foreground hover:text-foreground disabled:opacity-60"
                  aria-label="Ta bort bild"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        id={inputId}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) {
            void onUpload(files);
          }
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || isUploading || atLimit}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        {isUploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />}
        {atLimit ? 'Max 8 bilder' : 'Lägg till bilder'}
      </Button>
    </div>
  );
}

/* ── Types ── */
type EditorMode = 'grundinfo' | 'occurrence' | 'new-occurrence';

/* ── Helpers ── */
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mån' },
  { value: 2, label: 'Tis' },
  { value: 3, label: 'Ons' },
  { value: 4, label: 'Tors' },
  { value: 5, label: 'Fre' },
  { value: 6, label: 'Lör' },
  { value: 0, label: 'Sön' },
] as const;

function toLocalDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toLocalTimeInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim().length > 0) {
      return maybeMessage;
    }
  }
  return fallback;
}

/* ── Main component ── */
export default function ProducerFleaMarketsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);
  const { config: rollout, isLoading: rolloutLoading } = useFleaMarketRollout();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  /* ── Flea market CRUD ── */
  const { data: fleaMarkets = [], isLoading: fleaMarketsLoading } = useProducerFleaMarkets(farmId);
  const createFleaMarket = useCreateFleaMarket();
  const updateFleaMarket = useUpdateFleaMarket();
  const deleteFleaMarket = useDeleteFleaMarket();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newOccurrenceDraftSeed, setNewOccurrenceDraftSeed] = useState(0);
  const newOccurrenceDraftKey = `flea-market-occurrence-${selectedId ?? farmId ?? 'draft'}-${newOccurrenceDraftSeed}`;
  const newOccurrenceDraftSessionId = useDraftSessionId(newOccurrenceDraftKey);
  const selectedMarket = useMemo(() => {
    if (selectedId) return fleaMarkets.find((m) => m.id === selectedId) ?? null;
    return fleaMarkets[0] ?? null;
  }, [selectedId, fleaMarkets]);

  /* ── Editor mode ── */
  const [editorMode, setEditorMode] = useState<EditorMode>('grundinfo');
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null);

  /* ── Base form state ── */
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  /* ── Occurrence create state ── */
  const [occDate, setOccDate] = useState('');
  const [occStartTime, setOccStartTime] = useState('10:00');
  const [occEndTime, setOccEndTime] = useState('14:00');
  const [occDescriptionOverride, setOccDescriptionOverride] = useState('');

  /* ── Occurrence edit state ── */
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editDescriptionOverride, setEditDescriptionOverride] = useState('');

  /* ── Bulk create state ── */
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [bulkFromDate, setBulkFromDate] = useState('');
  const [bulkToDate, setBulkToDate] = useState('');
  const [bulkWeekdays, setBulkWeekdays] = useState<number[]>([6]);
  const [bulkStartTime, setBulkStartTime] = useState('10:00');
  const [bulkEndTime, setBulkEndTime] = useState('14:00');
  const [bulkNote, setBulkNote] = useState('');

  /* ── Mutations ── */
  const createOccurrence = useCreateFleaMarketOccurrence();
  const createBulkOccurrences = useCreateFleaMarketOccurrencesBulk();
  const updateOccurrence = useUpdateFleaMarketOccurrence();
  const deleteOccurrence = useDeleteFleaMarketOccurrence();

  /* ── Occurrences ── */
  const { data: occurrences = [], isLoading: occLoading } = useFleaMarketOccurrences(selectedMarket?.id);
  const { data: selectedOccurrenceGalleryAssets = [] } = useFleaMarketOccurrenceGalleryAssets(selectedOccurrenceId);
  const { data: draftOccurrenceGalleryAssets = [] } = useFleaMarketOccurrenceDraftGalleryAssets(newOccurrenceDraftSessionId);
  const sortedOccurrences = useMemo(() => sortOccurrencesNearestFirst(occurrences), [occurrences]);
  const nextOccurrence = useMemo(() => getNextUpcomingOccurrence(sortedOccurrences), [sortedOccurrences]);

  const selectedOccurrenceGalleryUpload = useMediaUpload({
    ownerType: 'flea_market_occurrence',
    ownerId: selectedOccurrenceId,
    draftSessionId: `flea-market-occurrence-${selectedOccurrenceId ?? 'none'}`,
    kind: 'gallery',
    maxFiles: 8,
  });

  const newOccurrenceGalleryUpload = useMediaUpload({
    ownerType: 'flea_market_occurrence',
    ownerId: null,
    draftSessionId: newOccurrenceDraftSessionId,
    kind: 'gallery',
    maxFiles: 8,
  });

  const selectedOccurrence = useMemo(
    () => occurrences.find((o) => o.id === selectedOccurrenceId) ?? null,
    [occurrences, selectedOccurrenceId],
  );

  /* ── Bulk validation ── */
  const bulkRows = useMemo(() => {
    if (!selectedMarket) return [];
    return buildBulkOccurrencePayloads({
      fleaMarketId: selectedMarket.id,
      fromDate: bulkFromDate,
      toDate: bulkToDate,
      weekdays: bulkWeekdays,
      startTime: bulkStartTime,
      endTime: bulkEndTime,
      note: bulkNote,
    });
  }, [selectedMarket, bulkFromDate, bulkToDate, bulkWeekdays, bulkStartTime, bulkEndTime, bulkNote]);

  /* Deduplicate bulk rows against existing occurrences */
  const bulkDedupPreview = useMemo(
    () => filterBulkDuplicates(bulkRows, occurrences),
    [bulkRows, occurrences],
  );

  const bulkValidationMessage = useMemo(() => {
    if (!bulkFromDate || !bulkToDate) return 'Välj från- och till-datum.';
    if (bulkFromDate > bulkToDate) return 'Från-datum kan inte vara efter till-datum.';
    if (bulkWeekdays.length === 0) return 'Välj minst en veckodag.';
    if (!bulkStartTime) return 'Starttid krävs.';
    if (hasInvalidTimeRange(bulkStartTime, bulkEndTime)) return 'Sluttid kan inte vara före starttid.';
    return null;
  }, [bulkFromDate, bulkToDate, bulkWeekdays, bulkStartTime, bulkEndTime]);

  /* ── Media uploads ── */
  const coverUpload = useMediaUpload({
    ownerType: 'flea_market',
    ownerId: selectedMarket?.id,
    draftSessionId: `flea-market-${selectedMarket?.id ?? farmId ?? 'draft'}`,
    kind: 'cover',
    maxFiles: 1,
  });

  const galleryUpload = useMediaUpload({
    ownerType: 'flea_market',
    ownerId: selectedMarket?.id,
    draftSessionId: `flea-market-${selectedMarket?.id ?? farmId ?? 'draft'}`,
    kind: 'gallery',
    maxFiles: 8,
  });

  const canEdit = hasCapability('canEditProducerInfo');

  /* ── Sync form from selected market ── */
  useEffect(() => {
    if (!selectedMarket) {
      setTitle('');
      setDescription('');
      setIsPublic(false);
      return;
    }
    setTitle(selectedMarket.title);
    setDescription(selectedMarket.description ?? '');
    setIsPublic(selectedMarket.status === 'public');
  }, [selectedMarket]);

  useEffect(() => {
    setEditorMode('grundinfo');
    setSelectedOccurrenceId(null);
  }, [selectedMarket?.id]);

  /* ── Handlers ── */
  const handleSelect = (id: string) => {
    setSelectedId(id);
    setEditorMode('grundinfo');
    setSelectedOccurrenceId(null);
    const market = fleaMarkets.find((m) => m.id === id);
    if (market) {
      setTitle(market.title);
      setDescription(market.description ?? '');
      setIsPublic(market.status === 'public');
    }
  };

  const handleSelectOccurrence = (id: string) => {
    const occ = occurrences.find((o) => o.id === id);
    if (!occ) return;
    setEditorMode('occurrence');
    setSelectedOccurrenceId(id);
    setEditDate(toLocalDateInput(occ.start_at));
    setEditStartTime(toLocalTimeInput(occ.start_at));
    setEditEndTime(toLocalTimeInput(occ.end_at));
    setEditDescriptionOverride(occ.description_override ?? occ.note ?? '');
  };

  const handleNewOccurrenceMode = () => {
    setEditorMode('new-occurrence');
    setSelectedOccurrenceId(null);
    setOccDate('');
    setOccStartTime('10:00');
    setOccEndTime('14:00');
    setOccDescriptionOverride('');
    setNewOccurrenceDraftSeed((current) => current + 1);
    setShowBulkCreate(false);
  };

  const invalidateOccurrenceMedia = async (occurrenceId: string) => {
    await queryClient.invalidateQueries({ queryKey: ['flea-market-occurrence-gallery-assets', occurrenceId] });
    await queryClient.invalidateQueries({ queryKey: ['flea-market-occurrence-gallery-map'] });
  };

  const handleUploadSelectedOccurrenceImages = async (files: File[]) => {
    if (!selectedOccurrenceId || files.length === 0) return;
    const result = await selectedOccurrenceGalleryUpload.uploadFiles(files);
    if (result.length > 0) {
      await invalidateOccurrenceMedia(selectedOccurrenceId);
    }
  };

  const handleRemoveSelectedOccurrenceImage = async (assetId: string) => {
    if (!selectedOccurrenceId) return;
    await deleteAsset(assetId);
    await invalidateOccurrenceMedia(selectedOccurrenceId);
  };

  const handleUploadDraftOccurrenceImages = async (files: File[]) => {
    if (files.length === 0) return;
    const result = await newOccurrenceGalleryUpload.uploadFiles(files);
    if (result.length > 0) {
      await queryClient.invalidateQueries({
        queryKey: ['flea-market-occurrence-draft-gallery-assets', newOccurrenceDraftSessionId],
      });
    }
  };

  const handleRemoveDraftOccurrenceImage = async (assetId: string) => {
    await deleteAsset(assetId);
    await queryClient.invalidateQueries({
      queryKey: ['flea-market-occurrence-draft-gallery-assets', newOccurrenceDraftSessionId],
    });
  };

  const handleCreate = async () => {
    if (!farmId || !producer?.place_id) {
      toast.error('Koppla gården till en plats först under Plats.');
      return;
    }
    try {
      const row = await createFleaMarket.mutateAsync({
        producer_id: farmId,
        place_id: producer.place_id,
        title: 'Loppis på gården',
        description: null,
        hero_media_asset_id: null,
        status: 'draft',
        created_by: null,
      });
      setSelectedId(row.id);
      setTitle(row.title);
      setDescription(row.description ?? '');
      setIsPublic(false);
      setEditorMode('grundinfo');
      toast.success('Loppis skapad');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte skapa loppis'));
    }
  };

  const handleSave = async () => {
    if (!selectedMarket) return;
    if (!title.trim()) {
      toast.error('Rubrik krävs');
      return;
    }
    try {
      await updateFleaMarket.mutateAsync({
        id: selectedMarket.id,
        title: title.trim(),
        description: normalizeTiptapHtml(description) || null,
        status: isPublic ? 'public' : 'draft',
        published_at: isPublic ? selectedMarket.published_at ?? new Date().toISOString() : null,
      });
      toast.success('Loppis sparad');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte spara loppis'));
    }
  };

  const handleTogglePublish = async () => {
    if (!selectedMarket) return;
    const newPublic = !isPublic;
    const newStatus = newPublic ? 'public' : 'draft';
    try {
      await updateFleaMarket.mutateAsync({
        id: selectedMarket.id,
        status: newStatus,
        published_at: newPublic ? selectedMarket.published_at ?? new Date().toISOString() : null,
      });
      setIsPublic(newPublic);
      toast.success(newPublic ? 'Loppis publicerad' : 'Loppis återställd till utkast');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte ändra status'));
    }
  };

  const handleDelete = async () => {
    if (!selectedMarket || !farmId) return;
    const confirmed = window.confirm('Ta bort loppisen och alla tillfällen?');
    if (!confirmed) return;
    try {
      await deleteFleaMarket.mutateAsync({ id: selectedMarket.id, producerId: farmId });
      setSelectedId(null);
      setTitle('');
      setDescription('');
      setIsPublic(false);
      toast.success('Loppis borttagen');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte ta bort loppis'));
    }
  };

  const handleAddOccurrence = async () => {
    if (!selectedMarket) return;
    const patch = buildOccurrenceEditPatch({
      date: occDate,
      startTime: occStartTime,
      endTime: occEndTime,
      descriptionOverride: occDescriptionOverride,
      imagesOverride: null,
    });
    if (!patch) {
      toast.error('Ange giltigt datum och tider. Sluttid kan inte vara före starttid.');
      return;
    }
    try {
      const row = await createOccurrence.mutateAsync({
        flea_market_id: selectedMarket.id,
        ...patch,
        images_override: patch.images_override ?? null,
        status: 'scheduled',
      });

      const draftAssets = await listAssetsByDraftSession(newOccurrenceDraftSessionId);
      const hasDraftOccurrenceImages = draftAssets.some(
        (asset) => asset.owner_type === 'flea_market_occurrence' && asset.kind === 'gallery',
      );

      if (hasDraftOccurrenceImages) {
        await finalizeDraftSession(newOccurrenceDraftSessionId, 'flea_market_occurrence', row.id);
        clearDraftSessionId(newOccurrenceDraftKey);
        setNewOccurrenceDraftSeed((current) => current + 1);
      }

      setOccDate('');
      setOccStartTime('10:00');
      setOccEndTime('14:00');
      setOccDescriptionOverride('');
      toast.success('Tillfälle tillagt');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte skapa tillfälle'));
    }
  };

  const toggleBulkWeekday = (weekday: number) => {
    setBulkWeekdays((current) =>
      current.includes(weekday)
        ? current.filter((item) => item !== weekday)
        : [...current, weekday].sort((a, b) => a - b),
    );
  };

  const handleBulkCreate = async () => {
    if (!selectedMarket) return;
    if (bulkValidationMessage) {
      toast.error(bulkValidationMessage);
      return;
    }
    if (bulkRows.length === 0) {
      toast.error('Inga tillfällen skapades. Kontrollera datumintervallet och valda veckodagar.');
      return;
    }
    const { payloads: dedupedRows, skippedCount } = bulkDedupPreview;
    if (dedupedRows.length === 0) {
      toast.info('Alla tillfällen finns redan. Inga nya skapades.');
      return;
    }
    try {
      await createBulkOccurrences.mutateAsync(
        dedupedRows.map((row) => ({
          ...row,
          description_override: row.note,
          images_override: null,
        })),
      );
      if (skippedCount > 0) {
        toast.success(`${dedupedRows.length} tillfällen tillagda. ${skippedCount} hoppades över (datum finns redan).`);
      } else {
        toast.success(`${dedupedRows.length} tillfällen tillagda`);
      }
      setShowBulkCreate(false);
      setBulkFromDate('');
      setBulkToDate('');
      setBulkWeekdays([6]);
      setBulkNote('');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte lägga till flera tillfällen'));
    }
  };

  const handleSaveOccurrenceEdit = async () => {
    if (!selectedOccurrenceId) return;
    const patch = buildOccurrenceEditPatch({
      date: editDate,
      startTime: editStartTime,
      endTime: editEndTime,
      descriptionOverride: editDescriptionOverride,
    });
    if (!patch) {
      toast.error('Ange giltig start/sluttid. Sluttid kan inte vara före starttid.');
      return;
    }
    try {
      await updateOccurrence.mutateAsync({ id: selectedOccurrenceId, ...patch });
      toast.success('Tillfället uppdaterades');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte spara ändringarna'));
    }
  };

  const handleCancelOccurrence = async (id: string) => {
    if (!selectedMarket) return;
    try {
      await updateOccurrence.mutateAsync({ id, status: 'cancelled', cancelled_at: new Date().toISOString() });
      toast.success('Tillfälle inställt');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte uppdatera tillfälle'));
    }
  };

  const handleDeleteOccurrence = async (id: string) => {
    if (!selectedMarket) return;
    try {
      const assetsToDelete = selectedOccurrenceId === id ? selectedOccurrenceGalleryAssets : [];
      if (assetsToDelete.length > 0) {
        await Promise.all(assetsToDelete.map((asset) => deleteAsset(asset.id)));
        await invalidateOccurrenceMedia(id);
      }

      await deleteOccurrence.mutateAsync({ id, fleaMarketId: selectedMarket.id });
      if (selectedOccurrenceId === id) {
        setEditorMode('grundinfo');
        setSelectedOccurrenceId(null);
      }
      toast.success('Tillfälle borttaget');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Kunde inte ta bort tillfälle'));
    }
  };

  /* ── Loading / feature flag guards ── */
  if (producerLoading || rolloutLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} title="Loppis">
        <div className="space-y-3 py-4">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </ProducerLayout>
    );
  }

  if (!rollout.enabled || !rollout.host_edit_enabled) {
    return (
      <ProducerLayout farmId={farmId || ''} title="Loppis">
        <ProducerSectionHeader title="Loppis" description="Funktionen är inte aktiverad just nu." isReadOnly />
        <Alert>
          <AlertDescription>Loppis är tillfälligt avstängd i denna miljö.</AlertDescription>
        </Alert>
      </ProducerLayout>
    );
  }

  /* ── Group occurrences for display ── */
  const upcoming = sortedOccurrences.filter((o) => getOccurrenceUiStatus(o) === 'planned');
  const cancelled = sortedOccurrences.filter((o) => getOccurrenceUiStatus(o) === 'cancelled');
  const completed = sortedOccurrences.filter((o) => getOccurrenceUiStatus(o) === 'completed');

  /* ── Sticky action bar content ── */
  const renderBaseActions = () => (
    <>
      <Button onClick={handleSave} disabled={!canEdit || updateFleaMarket.isPending} size="sm">
        {updateFleaMarket.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
        Spara
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleTogglePublish}
        disabled={!canEdit || updateFleaMarket.isPending}
      >
        {updateFleaMarket.isPending
          ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          : isPublic ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
        {isPublic ? 'Gör utkast' : 'Publicera'}
      </Button>
      {selectedMarket && (
        <Button variant="outline" size="sm" asChild>
          <Link to={`/loppis/${selectedMarket.id}`} target="_blank" rel="noopener noreferrer">
            Visa publik sida
          </Link>
        </Button>
      )}
    </>
  );

  const renderOccurrenceActions = () => {
    const uiStatus = selectedOccurrence ? getOccurrenceUiStatus(selectedOccurrence) : null;
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setEditorMode('grundinfo'); setSelectedOccurrenceId(null); }}
        >
          Avbryt
        </Button>
        <Button onClick={handleSaveOccurrenceEdit} disabled={!canEdit || updateOccurrence.isPending} size="sm">
          {updateOccurrence.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Spara tillfälle
        </Button>
        {uiStatus === 'planned' && selectedOccurrenceId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCancelOccurrence(selectedOccurrenceId)}
            disabled={!canEdit || updateOccurrence.isPending}
          >
            Ställ in
          </Button>
        )}
        {selectedOccurrenceId && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => handleDeleteOccurrence(selectedOccurrenceId)}
            disabled={!canEdit || deleteOccurrence.isPending}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Ta bort
          </Button>
        )}
      </>
    );
  };

  const renderNewOccurrenceActions = () => (
    <Button onClick={handleAddOccurrence} disabled={!canEdit || createOccurrence.isPending} size="sm">
      {createOccurrence.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
      Lägg till tillfälle
    </Button>
  );

  const currentActions =
    editorMode === 'grundinfo'
      ? renderBaseActions
      : editorMode === 'occurrence'
        ? renderOccurrenceActions
        : renderNewOccurrenceActions;

  /* ── Left nav item ── */
  const NavItem = ({
    label,
    icon,
    active,
    onClick,
    badge,
  }: {
    label: string;
    icon: React.ReactNode;
    active: boolean;
    onClick: () => void;
    badge?: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors text-left',
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
      )}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {badge}
      {active && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary/60" />}
    </button>
  );

  /* ── Occurrence list item (for left nav) ── */
  const OccurrenceNavItem = ({ occ }: { occ: typeof sortedOccurrences[number] }) => {
    const uiStatus = getOccurrenceUiStatus(occ);
    const isActive = editorMode === 'occurrence' && selectedOccurrenceId === occ.id;
    const isNext = nextOccurrence?.id === occ.id;
    return (
      <button
        type="button"
        onClick={() => handleSelectOccurrence(occ.id)}
        className={cn(
          'w-full flex items-start gap-2 rounded-md px-3 py-2 text-left transition-colors',
          isActive
            ? 'bg-primary/10 border border-primary/20'
            : 'hover:bg-muted/50 border border-transparent',
        )}
      >
        <CalendarDays className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm truncate', isActive && 'font-medium text-primary')}>
            {formatOccurrenceRangeSv(occ.start_at, occ.end_at)}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge
              variant={uiStatus === 'planned' ? 'secondary' : 'outline'}
              className="text-[10px] px-1.5 py-0"
            >
              {getOccurrenceUiLabel(uiStatus)}
            </Badge>
            {isNext && (
              <span className="text-[10px] text-primary font-medium">Nästa</span>
            )}
          </div>
        </div>
      </button>
    );
  };

  /* ── Render: Grundinfo content (left) ── */
  const renderGrundinfoContent = () => (
    <div className="space-y-10">
      {/* ── Section: Content ── */}
      <section className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Innehåll</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Rubrik och beskrivning som besökare ser.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="flea-title" className="sr-only">Rubrik</Label>
          <Input
            id="flea-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!canEdit}
            placeholder="T.ex. Loppis på gården"
            className="text-lg font-semibold h-12 border-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="flea-description" className="text-sm font-medium text-foreground">Beskrivning</Label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            disabled={!canEdit}
            producerId={farmId !== 'demo' ? farmId : undefined}
            preset="simple"
            minHeightPx={200}
            placeholder="Berätta kort vad besökare kan förvänta sig."
            expandable
            expandedTitle="Beskrivning"
            expandedSurface="flea_market_editor"
            expandedField="description"
          />
        </div>
      </section>

      {/* ── Section: Images ── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Bilder</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Bilder hjälper besökare att förstå känslan. Lägg till 1–3 bilder.
          </p>
        </div>

        <div className="rounded-lg border border-border/40 bg-card p-4">
          <MediaImageManager
            ownerType="flea_market"
            ownerId={selectedMarket!.id}
            producerId={farmId}
            draftSessionId={`flea-market-${selectedMarket!.id}`}
            coverUpload={coverUpload}
            galleryUpload={galleryUpload}
            disabled={!canEdit}
            helperText="Första bilden blir omslagsbild."
          />
        </div>
      </section>

      {/* ── Delete zone ── */}
      <div className="pt-4 border-t border-border/30">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={!canEdit || deleteFleaMarket.isPending}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Ta bort loppis
        </Button>
      </div>
    </div>
  );

  /* ── Render: Grundinfo sidebar (right: preview + settings) ── */
  const renderGrundinfoSidebar = () => (
    <div className="space-y-5">
      {/* Preview */}
      <EditorSidebarSection heading="Förhandsvisning">
        <FleaMarketPreviewCard
          title={title}
          description={description}
          fleaMarketId={selectedMarket?.id}
          nextOccurrenceLabel={
            nextOccurrence
              ? formatOccurrenceRangeSv(nextOccurrence.start_at, nextOccurrence.end_at)
              : null
          }
        />
      </EditorSidebarSection>

      {/* Settings */}
      <EditorSidebarSection heading="Inställningar">
        <EditorSidebarCard>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Synlighet</span>
            <Badge variant={isPublic ? 'default' : 'secondary'} className="text-[11px]">
              {isPublic ? 'Publik' : 'Utkast'}
            </Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTogglePublish}
            disabled={!canEdit || updateFleaMarket.isPending}
            className="w-full"
          >
            {updateFleaMarket.isPending
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : isPublic ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {isPublic ? 'Gör utkast' : 'Publicera'}
          </Button>
          {selectedMarket && (
            <Button variant="ghost" size="sm" className="w-full text-muted-foreground" asChild>
              <Link to={`/loppis/${selectedMarket.id}`} target="_blank" rel="noopener noreferrer">
                Visa publik sida
              </Link>
            </Button>
          )}
        </EditorSidebarCard>
      </EditorSidebarSection>
    </div>
  );

  /* ── Render: Occurrence editor ── */
  const renderOccurrenceEditor = () => {
    if (!selectedOccurrence) {
      return (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Tillfället hittades inte.
        </div>
      );
    }
    const uiStatus = getOccurrenceUiStatus(selectedOccurrence);
    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">Redigera tillfälle</h2>
            <Badge variant={uiStatus === 'planned' ? 'secondary' : 'outline'}>
              {getOccurrenceUiLabel(uiStatus)}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formatOccurrenceRangeSv(selectedOccurrence.start_at, selectedOccurrence.end_at)}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Datum</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!canEdit || updateOccurrence.isPending}
                  className={cn('w-full justify-start text-left font-normal', !editDate && 'text-muted-foreground')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {editDate ? format(new Date(`${editDate}T00:00:00`), 'PPP', { locale: sv }) : 'Välj datum'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={editDate ? new Date(`${editDate}T00:00:00`) : undefined}
                  onSelect={(day) => setEditDate(day ? format(day, 'yyyy-MM-dd') : '')}
                  disabled={(day) => day < new Date(new Date().toDateString())}
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-3 grid-cols-2">
            <div className="space-y-1.5">
              <Label>Starttid</Label>
              <Input
                type="time"
                value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)}
                disabled={!canEdit || updateOccurrence.isPending}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Sluttid (valfritt)</Label>
              <Input
                type="time"
                value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)}
                disabled={!canEdit || updateOccurrence.isPending}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Text för detta tillfälle (valfritt)</Label>
            <RichTextEditor
              value={editDescriptionOverride}
              onChange={setEditDescriptionOverride}
              disabled={!canEdit || updateOccurrence.isPending}
              producerId={farmId !== 'demo' ? farmId : undefined}
              preset="simple"
              minHeightPx={140}
              placeholder="Kort text om tillfället"
            />
            <p className="text-xs text-muted-foreground">Om tomt används loppisens beskrivning.</p>
          </div>

          <OccurrenceImagesField
            assets={selectedOccurrenceGalleryAssets}
            disabled={!canEdit || updateOccurrence.isPending}
            isUploading={selectedOccurrenceGalleryUpload.isUploading}
            onUpload={handleUploadSelectedOccurrenceImages}
            onRemove={handleRemoveSelectedOccurrenceImage}
            helperText="Dessa bilder kompletterar loppisens omslagsbild och visas bara för detta tillfälle."
            inputId="flea-occurrence-images-upload-edit"
          />
        </div>
      </div>
    );
  };

  /* ── Render: New occurrence editor ── */
  const renderNewOccurrenceEditor = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Nytt tillfälle</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Lägg till ett datum när loppisen är öppen.</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Datum</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={!canEdit}
                className={cn('w-full justify-start text-left font-normal', !occDate && 'text-muted-foreground')}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {occDate ? format(new Date(`${occDate}T00:00:00`), 'PPP', { locale: sv }) : 'Välj datum'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={occDate ? new Date(`${occDate}T00:00:00`) : undefined}
                onSelect={(day) => setOccDate(day ? format(day, 'yyyy-MM-dd') : '')}
                disabled={(day) => day < new Date(new Date().toDateString())}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="grid gap-3 grid-cols-2">
          <div className="space-y-1.5">
            <Label>Starttid</Label>
            <Input type="time" value={occStartTime} onChange={(e) => setOccStartTime(e.target.value)} disabled={!canEdit} />
          </div>
          <div className="space-y-1.5">
            <Label>Sluttid (valfritt)</Label>
            <Input type="time" value={occEndTime} onChange={(e) => setOccEndTime(e.target.value)} disabled={!canEdit} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Text för detta tillfälle (valfritt)</Label>
          <RichTextEditor
            value={occDescriptionOverride}
            onChange={setOccDescriptionOverride}
            disabled={!canEdit}
            producerId={farmId !== 'demo' ? farmId : undefined}
            preset="simple"
            minHeightPx={140}
            placeholder="T.ex. endast utomhus denna gång"
          />
          <p className="text-xs text-muted-foreground">Om tomt används loppisens beskrivning.</p>
        </div>

        <OccurrenceImagesField
          assets={draftOccurrenceGalleryAssets}
          disabled={!canEdit || createOccurrence.isPending}
          isUploading={newOccurrenceGalleryUpload.isUploading}
          onUpload={handleUploadDraftOccurrenceImages}
          onRemove={handleRemoveDraftOccurrenceImage}
          helperText="Dessa bilder kompletterar loppisens omslagsbild och visas bara för detta tillfälle."
          inputId="flea-occurrence-images-upload-new"
        />
      </div>

      {/* Bulk create */}
      <div className="border-t border-border/50 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowBulkCreate((prev) => !prev)}
          disabled={!canEdit || createBulkOccurrences.isPending}
        >
          {showBulkCreate ? 'Dölj flera' : 'Lägg till flera samtidigt'}
        </Button>

        {showBulkCreate && (
          <div className="rounded-lg border border-border/70 p-3 space-y-3 mt-3">
            <div className="grid gap-3 grid-cols-2">
              <div className="space-y-1.5">
                <Label>Från datum</Label>
                <Input type="date" value={bulkFromDate} onChange={(e) => setBulkFromDate(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="space-y-1.5">
                <Label>Till datum</Label>
                <Input type="date" value={bulkToDate} onChange={(e) => setBulkToDate(e.target.value)} disabled={!canEdit} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Veckodagar</Label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_OPTIONS.map((day) => (
                  <Button
                    key={day.value}
                    type="button"
                    variant={bulkWeekdays.includes(day.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleBulkWeekday(day.value)}
                    disabled={!canEdit}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 grid-cols-2">
              <div className="space-y-1.5">
                <Label>Starttid</Label>
                <Input type="time" value={bulkStartTime} onChange={(e) => setBulkStartTime(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="space-y-1.5">
                <Label>Sluttid (valfritt)</Label>
                <Input type="time" value={bulkEndTime} onChange={(e) => setBulkEndTime(e.target.value)} disabled={!canEdit} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Text för tillfällen (valfritt)</Label>
              <Input value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} disabled={!canEdit} placeholder="T.ex. loppis i ladan" />
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {bulkValidationMessage
                  ? bulkValidationMessage
                  : bulkRows.length === 0
                    ? 'Inga tillf\u00e4llen matchar valda veckodagar i intervallet.'
                    : bulkDedupPreview.skippedCount > 0
                      ? `${bulkDedupPreview.payloads.length} tillf\u00e4llen skapas. ${bulkDedupPreview.skippedCount} hoppas \u00f6ver (datum finns redan).`
                      : `${bulkRows.length} tillf\u00e4llen kommer att skapas.`}
              </p>
              <Button
                onClick={handleBulkCreate}
                disabled={!canEdit || !!bulkValidationMessage || bulkDedupPreview.payloads.length === 0 || createBulkOccurrences.isPending}
                size="sm"
              >
                {createBulkOccurrences.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Skapa flera
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  /* ── Empty / no market selected ── */
  const renderEmptyEditor = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <CalendarDays className="h-10 w-10 text-muted-foreground/40 mb-3" />
      <p className="text-sm text-muted-foreground">Välj eller skapa en loppis för att börja.</p>
    </div>
  );

  /* ── Left sidebar content ── */
  const renderLeftNav = () => (
    <div className="space-y-4">
      {/* Flea market selector (if multiple) */}
      {fleaMarketsLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : fleaMarkets.length > 1 ? (
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground px-1 mb-1">Loppisar</p>
          {fleaMarkets.map((market) => {
            const active = selectedMarket?.id === market.id;
            return (
              <button
                key={market.id}
                onClick={() => handleSelect(market.id)}
                className={cn(
                  'w-full text-left rounded-md px-3 py-2 text-sm transition-colors',
                  active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/50',
                )}
                type="button"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{market.title}</span>
                  <Badge variant={market.status === 'public' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 shrink-0">
                    {market.status === 'public' ? 'Publik' : 'Utkast'}
                  </Badge>
                </div>
              </button>
            );
          })}
        </div>
      ) : fleaMarkets.length === 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Inga loppisar ännu.</p>
          <Button onClick={handleCreate} disabled={!canEdit || !producer?.place_id || createFleaMarket.isPending} className="w-full" variant="outline" size="sm">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Skapa loppis
          </Button>
        </div>
      ) : null}

      {/* Nav items when a market is selected */}
      {selectedMarket && (
        <>
          {/* Market title + status */}
          {fleaMarkets.length <= 1 && (
            <div className="px-1">
              <p className="text-sm font-medium text-foreground truncate">{selectedMarket.title}</p>
              {nextOccurrence && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Nästa: {formatOccurrenceRangeSv(nextOccurrence.start_at, nextOccurrence.end_at)}
                </p>
              )}
            </div>
          )}

          <div className="border-t border-border/40 pt-3 space-y-0.5">
            <NavItem
              label="Grundinfo"
              icon={<Info className="h-4 w-4 shrink-0" />}
              active={editorMode === 'grundinfo'}
              onClick={() => { setEditorMode('grundinfo'); setSelectedOccurrenceId(null); }}
            />
          </div>

          {/* Occurrences section */}
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground px-1">Tillfällen
              {sortedOccurrences.length > 0 && (
                <span className="ml-1.5 normal-case tracking-normal font-normal">
                  ({upcoming.length} planerade)
                </span>
              )}
            </p>

            {occLoading ? (
              <div className="space-y-1.5 px-1">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sortedOccurrences.length === 0 ? (
              <p className="text-xs text-muted-foreground px-3 py-2">Inga tillfällen ännu.</p>
            ) : (
              <div className="space-y-0.5">
                {upcoming.length > 0 && upcoming.map((occ) => <OccurrenceNavItem key={occ.id} occ={occ} />)}
                {cancelled.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 px-3 pt-2">Inställda</p>
                    {cancelled.map((occ) => <OccurrenceNavItem key={occ.id} occ={occ} />)}
                  </>
                )}
                {completed.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 px-3 pt-2">Genomförda</p>
                    {completed.map((occ) => <OccurrenceNavItem key={occ.id} occ={occ} />)}
                  </>
                )}
              </div>
            )}

            <div className="pt-1 px-1">
              <Button
                variant={editorMode === 'new-occurrence' ? 'default' : 'outline'}
                size="sm"
                className="w-full"
                onClick={handleNewOccurrenceMode}
                disabled={!canEdit}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nytt tillfälle
              </Button>
            </div>
          </div>


        </>
      )}
    </div>
  );

  /* ── Right editor content (for occurrence modes) ── */
  const renderEditor = () => {
    if (!selectedMarket) return renderEmptyEditor();
    switch (editorMode) {
      case 'grundinfo':
        return renderGrundinfoContent();
      case 'occurrence':
        return renderOccurrenceEditor();
      case 'new-occurrence':
        return renderNewOccurrenceEditor();
      default:
        return renderEmptyEditor();
    }
  };

  const isGrundinfoDesktop = !isMobile && editorMode === 'grundinfo' && !!selectedMarket;

  /* ── Sticky action bar for grundinfo (Save only — publish moved to sidebar) ── */
  const renderGrundinfoDesktopActions = () => (
    <>
      <Button onClick={handleSave} disabled={!canEdit || updateFleaMarket.isPending} size="sm">
        {updateFleaMarket.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
        Spara
      </Button>
    </>
  );

  /* ── Main layout ── */
  return (
    <ProducerLayout farmId={farmId || ''} title="Loppis">
      <ProducerSectionHeader
        title="Loppis"
        description="Skapa en återkommande loppis med kommande tillfällen."
        isReadOnly={!canEdit}
      />

      {!producer?.place_id && (
        <Alert className="mb-4">
          <AlertDescription>
            Du behöver först koppla gården till en plats under{' '}
            <Link to={`/app/producer/farm/${farmId}/plats`} className="underline">
              Plats
            </Link>{' '}
            för att kunna skapa loppis.
          </AlertDescription>
        </Alert>
      )}

      {/* Desktop layout */}
      {!isMobile ? (
        <div className={cn(
          'grid gap-x-8',
          isGrundinfoDesktop
            ? 'lg:grid-cols-[260px_1fr_340px]'
            : 'lg:grid-cols-[260px_1fr]',
        )}>
          {/* Left nav */}
          <div className="lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-2 lg:py-1">
            {renderLeftNav()}
          </div>

          {/* Center editor */}
          <div className="min-w-0">
            {/* Sticky desktop action bar */}
            {selectedMarket && (
              <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border/40 -mx-1 px-1 py-2.5 mb-6 flex items-center justify-end gap-2 flex-wrap">
                {isGrundinfoDesktop ? renderGrundinfoDesktopActions() : currentActions()}
              </div>
            )}
            {renderEditor()}
          </div>

          {/* Right sidebar (grundinfo only) */}
          {isGrundinfoDesktop && (
            <div className="lg:sticky lg:top-24 lg:self-start">
              {renderGrundinfoSidebar()}
            </div>
          )}
        </div>
      ) : (
        /* Mobile: stacked layout */
        <div className="space-y-4">
          {/* Compact mobile nav */}
          <Card>
            <CardContent className="pt-4 pb-3">
              {renderLeftNav()}
            </CardContent>
          </Card>

          {/* Editor */}
          <Card>
            <CardContent className="pt-4 pb-6">
              {renderEditor()}
            </CardContent>
          </Card>

          {/* Mobile preview + settings (grundinfo only) */}
          {editorMode === 'grundinfo' && selectedMarket && (
            <Card>
              <CardContent className="pt-4 pb-4">
                {renderGrundinfoSidebar()}
              </CardContent>
            </Card>
          )}

          {/* Sticky bottom actions */}
          {selectedMarket && (
            <StickyBottomBar mobileOnly>
              {currentActions()}
            </StickyBottomBar>
          )}
        </div>
      )}
    </ProducerLayout>
  );
}
